/**
 * The audit trail, rendered as it is stored.
 *
 * Every transition, in order, with who did it — the answer to "who approved this, and when",
 * which is the question that arrives six months later when nobody remembers. Actions are
 * relabelled for reading; the `detail` blob is shown raw underneath, because a summary of an
 * audit record is not an audit record.
 */
import { useState } from 'react';
import { Badge, Card, Table, Td, Th } from '@/components/ui';
import { formatInstant } from '@/lib/dates';
import type { AuditEntry } from '@/lib/types';

const READABLE: Record<string, string> = {
  created: 'Draft created',
  attendees_set: 'Namelist set',
  updated: 'Details edited',
  submitted: 'Submitted',
  sponsor_endorsed: 'Endorsed by Sponsor',
  sponsor_returned: 'Returned by Sponsor',
  sponsor_rejected: 'Rejected by Sponsor',
  hr_took_review: 'Taken for HR review',
  hr_returned: 'Returned by HR',
  clarification_requested: 'Question asked',
  clarification_answered: 'Question answered',
  clarification_closed: 'Question closed',
  exception_raised: 'Exception raised',
  exception_confirmed: 'Exception accepted',
  exception_declined: 'Exception declined',
  confirmed: 'Budget confirmed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  withdrawn: 'Withdrawn',
  reopened: 'Reopened',
  expired: 'Closed out',
  amended: 'Namelist amended by HR',
  evidence_set: 'Evidence link added',
  archive_set: 'Archive link added',
  hr_verified: 'Passed HR verification',
  director_approved: 'Approved by HR Director',
  director_returned: 'Returned by HR Director',
  director_rejected: 'Rejected by HR Director',
  paid: 'Marked paid',
  senior_topup_set: 'Senior top-up recorded',
};

export function AuditTrail({ entries }: { entries: readonly AuditEntry[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <Card title={`History — ${entries.length} ${entries.length === 1 ? 'event' : 'events'}`}>
      {entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing has happened yet.</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>When</Th>
              <Th>What</Th>
              <Th>Status</Th>
              <Th>By</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const hasDetail = Object.keys(entry.detail ?? {}).length > 0;
              return (
                <tr key={entry.id}>
                  <Td className="text-xs whitespace-nowrap">{formatInstant(entry.created_at)}</Td>
                  <Td>{READABLE[entry.action] ?? entry.action.replace(/_/g, ' ')}</Td>
                  <Td className="text-xs">
                    {entry.from_status &&
                    entry.to_status &&
                    entry.from_status !== entry.to_status ? (
                      <span className="text-muted-foreground">
                        {entry.from_status} → {entry.to_status}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{entry.to_status ?? '—'}</span>
                    )}
                  </Td>
                  <Td className="text-xs">
                    {entry.actor_source === 'system' ? (
                      <Badge>system</Badge>
                    ) : (
                      `user #${entry.actor_user_id ?? '?'}`
                    )}
                  </Td>
                  <Td>
                    {hasDetail ? (
                      <button
                        type="button"
                        className="text-xs underline underline-offset-4"
                        aria-expanded={expanded === entry.id}
                        onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                      >
                        {expanded === entry.id ? 'hide' : 'detail'}
                      </button>
                    ) : null}
                    {expanded === entry.id ? (
                      <pre className="text-muted-foreground mt-1 max-w-md overflow-x-auto text-xs">
                        {JSON.stringify(entry.detail, null, 2)}
                      </pre>
                    ) : null}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
